import React, { useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Checkbox } from '../ui/checkbox';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { CheckSquare, XSquare, Loader2, Trash2, FolderKey } from 'lucide-react';
import { toast } from 'sonner';
import { accessControlService } from '../../services/accessControlService';
import { FileAccessEditor } from './FileAccessEditor';

interface UserData {
  user_id: string;
  email: string;
  features: Record<string, boolean>;
}

interface PermissionsPivotProps {
  users: UserData[];
  isLoading: boolean;
  onPermissionChange: (userId: string, feature: string, enabled: boolean) => Promise<void>;
  onBulkChange: (userId: string, enableAll: boolean) => Promise<void>;
  onRefresh: () => void;
  onRemoveMember?: (userId: string, email: string) => Promise<void>;
  showRemoveButton?: boolean;
  currentUserId?: string;
}

const FEATURES = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'upload', label: 'Upload' },
  { key: 'analyze', label: 'Analyze' },
  { key: 'visualize', label: 'Visualize' },
  { key: 'introspect', label: 'Introspect' },
  { key: 'developer-api', label: 'Developer API' },
  { key: 'ai-connections', label: 'AI Connections' },
  { key: 'integrations', label: 'Integrations' },
  { key: 'usage', label: 'Usage' },
  { key: 'profile', label: 'Profile' },
  { key: 'stats-dashboard', label: 'Stats Dashboard' },
  { key: 'admin', label: 'Admin' },
];

export const PermissionsPivot: React.FC<PermissionsPivotProps> = ({
  users,
  isLoading,
  onPermissionChange,
  onBulkChange,
  onRefresh,
  onRemoveMember,
  showRemoveButton = false,
  currentUserId,
}) => {
  const [updatingCell, setUpdatingCell] = useState<string | null>(null);
  const [updatingUser, setUpdatingUser] = useState<string | null>(null);
  const [fileAccessUser, setFileAccessUser] = useState<{ id: string; email: string } | null>(null);

  const handleCheckboxChange = async (userId: string, feature: string, currentValue: boolean) => {
    const cellKey = `${userId}-${feature}`;
    setUpdatingCell(cellKey);

    try {
      await onPermissionChange(userId, feature, !currentValue);
    } catch (error) {
      toast.error(`Failed to update permission: ${error}`);
    } finally {
      setUpdatingCell(null);
    }
  };

  const handleEnableAll = async (userId: string) => {
    setUpdatingUser(userId);
    try {
      await onBulkChange(userId, true);
      toast.success('All features enabled');
    } catch (error) {
      toast.error(`Failed to enable all features: ${error}`);
    } finally {
      setUpdatingUser(null);
    }
  };

  const handleDisableAll = async (userId: string) => {
    setUpdatingUser(userId);
    try {
      await onBulkChange(userId, false);
      toast.success('All features disabled (except Profile)');
    } catch (error) {
      toast.error(`Failed to disable all features: ${error}`);
    } finally {
      setUpdatingUser(null);
    }
  };

  const getEnabledCount = (user: UserData): number => {
    if (!user.features) return 0;
    return Object.values(user.features).filter(v => v === true).length;
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }

  if (users.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <p className="text-lg mb-2">No users found</p>
        <p className="text-sm">Users will appear here once they sign up for BigSplice</p>
      </div>
    );
  }

  return (
    <>
    <div className="border rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead className="sticky left-0 z-20 bg-muted/50 min-w-[250px] border-r">
                User
              </TableHead>
              <TableHead className="text-center border-r min-w-[100px]">Actions</TableHead>
              {FEATURES.map(feature => (
                <TableHead key={feature.key} className="text-center min-w-[120px]">
                  {feature.label}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map(user => {
              const enabledCount = getEnabledCount(user);
              const isUpdating = updatingUser === user.user_id;
              const isCurrentUser = currentUserId === user.user_id;

              return (
                <TableRow key={user.user_id} className="hover:bg-muted/30">
                  <TableCell className="sticky left-0 z-10 bg-background border-r font-medium">
                    <div className="flex flex-col gap-1">
                      <span className="text-sm">
                        {user.email}
                        {isCurrentUser && <span className="text-muted-foreground ml-1">(you)</span>}
                      </span>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">
                          {enabledCount} / {FEATURES.length} enabled
                        </Badge>
                        {user.features?.admin && (
                          <Badge variant="default" className="text-xs">
                            Admin
                          </Badge>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="border-r">
                    <div className="flex gap-1 justify-center">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEnableAll(user.user_id)}
                        disabled={isUpdating || isCurrentUser}
                        className="h-7 px-2 text-xs"
                        title={isCurrentUser ? "Cannot modify your own permissions" : "Enable all features"}
                      >
                        {isUpdating ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <CheckSquare className="h-3 w-3 mr-1" />
                        )}
                        All
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDisableAll(user.user_id)}
                        disabled={isUpdating || isCurrentUser}
                        className="h-7 px-2 text-xs"
                        title={isCurrentUser ? "Cannot modify your own permissions" : "Disable all features"}
                      >
                        {isUpdating ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <XSquare className="h-3 w-3 mr-1" />
                        )}
                        None
                      </Button>
                      {!isCurrentUser && !user.features?.admin && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setFileAccessUser({ id: user.user_id, email: user.email })}
                          disabled={isUpdating}
                          className="h-7 px-2 text-xs"
                          title="Manage file access"
                        >
                          <FolderKey className="h-3 w-3 mr-1" />
                          Files
                        </Button>
                      )}
                      {showRemoveButton && onRemoveMember && users.length > 1 && !isCurrentUser && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onRemoveMember(user.user_id, user.email)}
                          disabled={isUpdating}
                          className="h-7 px-2 text-xs text-destructive hover:text-destructive hover:bg-destructive/10"
                          title="Remove from organization"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                  {FEATURES.map(feature => {
                    const cellKey = `${user.user_id}-${feature.key}`;
                    const isChecked = user.features?.[feature.key] === true;
                    const isCellUpdating = updatingCell === cellKey;

                    return (
                      <TableCell key={feature.key} className="text-center">
                        <div className="flex items-center justify-center">
                          {isCellUpdating ? (
                            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                          ) : (
                            <Checkbox
                              checked={isChecked}
                              onCheckedChange={() =>
                                handleCheckboxChange(user.user_id, feature.key, isChecked)
                              }
                              disabled={isUpdating || isCurrentUser}
                              title={isCurrentUser ? "Cannot modify your own permissions" : undefined}
                            />
                          )}
                        </div>
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>

    {/* File Access Editor Modal */}
    {fileAccessUser && (
      <FileAccessEditor
        userId={fileAccessUser.id}
        userEmail={fileAccessUser.email}
        onClose={() => setFileAccessUser(null)}
        onSaved={() => setFileAccessUser(null)}
      />
    )}
    </>
  );
};
